<script>
  /**
   * Drag handle for adjusting block width (track).
   *
   * Mirrors BlockSpacingHandle: five snap positions, dragged instead of picked from a
   * menu. Spacing is dragged vertically on the top/bottom edges, width horizontally on
   * the left/right edges, and in both cases dragging outwards means "more".
   *
   * The handle renders for every selected block, including blocks whose layout can't take
   * a width of its own. Those get a disabled handle: still anchored to the edge, so the
   * affordance stays discoverable, but visibly inert and non-interactive. Absent handles
   * read as "the feature is broken"; a dimmed, dashed one reads as "not here".
   */
  import { load_sounds, play_sound } from '#lib/handle_sounds.js';

  let {
    /** @type {HTMLElement} The block element to position relative to */
    anchor,
    /** @type {string} Current track value */
    track,
    /** @type {string[]} Selectable tracks, ordered narrow -> wide */
    tracks = [],
    /** @type {'left'|'right'} Which edge the handle sits on */
    position = 'left',
    /** @type {number} Viewport x of the track edge this handle sits on, in px */
    edge_x = 0,
    /** @type {number} Viewport y to sit at, already clamped to the block by the parent */
    edge_y = 0,
    /** @type {boolean} Render inert: still on the edge, but not draggable */
    disabled = false,
    /** @type {string} Why the handle is disabled, shown as a tooltip on hover */
    disabled_reason = '',
    /** @type {(value: string) => void} Callback when the track changes */
    onchange,
  } = $props();

  /** Pointer travel per width step */
  const PIXELS_PER_STEP = 40;

  let is_dragging = $state(false);
  let is_hovered = $state(false);
  let drag_start_x = $state(0);
  let drag_start_track = $state('');
  let handle_ref = $state();

  let track_index = $derived(Math.max(0, tracks.indexOf(track)));

  // Having nothing to step through amounts to the same thing as being switched off, so a
  // block with one track (or none) disables itself without the parent having to say so.
  let is_disabled = $derived(disabled || tracks.length < 2);

  // Drag feedback is suppressed while disabled, so a drag that was in flight when the
  // handle went inert can't leave the grabbing cursor or the value label behind.
  let is_active_drag = $derived(is_dragging && !is_disabled);

  /** Tooltip text - only while disabled, and only if a reason was given */
  let title_text = $derived(is_disabled && disabled_reason ? disabled_reason : undefined);

  /** Accessible name, which doubles as the disabled explanation for screen readers */
  let aria_label = $derived(
    is_disabled
      ? `Block width not adjustable${disabled_reason ? `: ${disabled_reason}` : ''}`
      : `Adjust block width (${position})`
  );

  /**
   * Handle pointer down - start drag
   * @param {PointerEvent} event
   */
  function handle_pointerdown(event) {
    // Swallow the press even while disabled: clicking a dead handle should do nothing,
    // rather than fall through to the canvas and deselect the block it belongs to.
    event.preventDefault();
    event.stopPropagation();

    if (is_disabled) return;

    load_sounds();

    is_dragging = true;
    drag_start_x = event.clientX;
    drag_start_track = track;

    handle_ref?.setPointerCapture(event.pointerId);
  }

  /**
   * Handle pointer move - update track during drag
   * @param {PointerEvent} event
   */
  function handle_pointermove(event) {
    if (is_disabled || !is_dragging) return;

    // Dragging outwards (left handle to the left, right handle to the right) widens
    const delta_x = event.clientX - drag_start_x;
    const outwards = position === 'left' ? -delta_x : delta_x;

    const start_index = Math.max(0, tracks.indexOf(drag_start_track));
    const step_change = Math.round(outwards / PIXELS_PER_STEP);
    const new_index = Math.max(0, Math.min(tracks.length - 1, start_index + step_change));
    const new_track = tracks[new_index];

    if (new_track !== track) {
      play_sound(new_index);
      onchange?.(new_track);
    }
  }

  /**
   * Handle pointer up - end drag
   * @param {PointerEvent} event
   */
  function handle_pointerup(event) {
    if (!is_dragging) return;
    is_dragging = false;
    handle_ref?.releasePointerCapture(event.pointerId);
  }

  /**
   * Keyboard support: arrow keys step through the widths
   * @param {KeyboardEvent} event
   */
  function handle_keydown(event) {
    if (is_disabled) return;

    const outwards = position === 'left' ? -1 : 1;
    let step = 0;
    if (event.key === 'ArrowLeft') step = -1 * outwards;
    else if (event.key === 'ArrowRight') step = outwards;
    else return;

    event.preventDefault();
    const new_index = Math.max(0, Math.min(tracks.length - 1, track_index + step));
    if (tracks[new_index] !== track) onchange?.(tracks[new_index]);
  }
</script>

{#if anchor}
  <!-- The role and tabindex are dropped while disabled, which the compiler can't see
       through, so it reads the element as a plain div carrying a tabindex. -->
  <!-- svelte-ignore a11y_no_static_element_interactions, a11y_no_noninteractive_tabindex -->
  <div
    bind:this={handle_ref}
    class="width-handle {position}"
    style:--handle-x="{edge_x}px"
    style:--handle-y="{edge_y}px"
    class:dragging={is_active_drag}
    class:disabled={is_disabled}
    class:visible={!is_disabled && (is_hovered || is_dragging)}
    title={title_text}
    onpointerdown={handle_pointerdown}
    onpointermove={handle_pointermove}
    onpointerup={handle_pointerup}
    onpointercancel={handle_pointerup}
    onkeydown={handle_keydown}
    onmouseenter={() => is_hovered = true}
    onmouseleave={() => !is_dragging && (is_hovered = false)}
    role={is_disabled ? undefined : 'slider'}
    aria-label={aria_label}
    aria-disabled={is_disabled ? 'true' : undefined}
    aria-valuenow={is_disabled ? undefined : track_index}
    aria-valuemin={is_disabled ? undefined : 0}
    aria-valuemax={is_disabled ? undefined : tracks.length - 1}
    aria-valuetext={is_disabled ? undefined : track}
    tabindex={is_disabled ? undefined : -1}
  >
    <div class="handle-line"></div>
  </div>
{/if}

<style>
  .width-handle {
    position: fixed;
    margin: 0;
    border: none;
    z-index: 100;

    /* Pinned to the middle of the viewport, not the middle of the block: a tall block would
       otherwise put its handles off-screen and you would have to scroll away from the edge
       you are trying to drag just to find them. Horizontal position is the measured track
       edge, passed in as px - the block box spans the whole page, so anchoring to it put the
       handles on the page margins rather than on the edges they actually move. */
    top: var(--handle-y);
    transform: translateY(-50%);
    left: var(--handle-x);
    translate: -50% 0;

    /* Layout */
    padding: 24px 8px;
    background: transparent;
    border-radius: var(--round);
    cursor: ew-resize;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.5;
    transition: opacity 0.15s ease;


    &.visible, &:hover, &:focus-visible {
      opacity: 1;
    }

    &.dragging {
      opacity: 1;
      cursor: grabbing;
    }

    /* Disabled: the same handle, just quieter. Keeping the silhouette identical means the
       eye reads "not available here" rather than "a different control". */
    &.disabled {
      cursor: not-allowed;
      opacity: 0.25;
      filter: saturate(0.15);
    }

    &.disabled:hover {
      opacity: 0.5;
    }
  }

  .handle-line {
    width: 4px;
    height: 40px;
    background: var(--editing);
    border-radius: 2px;
    box-shadow: 0 1px 3px oklch(0 0 0 / 0.2);
    transition: height 0.15s ease;

    .width-handle:not(.disabled):hover &,
    .width-handle.dragging & {
      height: 56px;
    }
  }

</style>
